import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Globe,
  HardHat,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Search,
  Share2,
  Star,
  UserRound,
  X,
} from "lucide-react";
import { api, ApiError, type ContractorDirectoryRow } from "../api";
import { hasAnyRole, useAuth } from "../auth";
import { Button, Card, PageHeader, cx } from "../ui";

// Reference directory of construction companies that have worked in
// the building. Backed by GET /api/contractor-directory, which
// aggregates every non-removed project engagement in the caller's
// building(s): contact info, project counts, and types of work.

const DISCIPLINE_LABELS: Record<string, string> = {
  general: "General Contractor",
  plumbing: "Plumbing",
  electrical: "Electrical",
  hvac: "HVAC",
  other: "Other",
};

const ROLE_LABELS: Record<string, string> = {
  general: "General Contractor",
  plumbing: "Plumber",
  electrical: "Electrician",
  hvac: "HVAC",
};

function disciplineLabel(code: string): string {
  return DISCIPLINE_LABELS[code] ?? code.replaceAll("_", " ");
}

/**
 * Rating line under the company name: the anonymous average from
 * other owners, and — when the signed-in owner engaged this company
 * on one of their unit's projects — a clickable star picker.
 * Clicking your current rating withdraws it.
 */
function RatingRow({
  row,
  busy,
  onRate,
}: {
  row: ContractorDirectoryRow;
  busy: boolean;
  onRate: (stars: number) => void;
}) {
  if (row.ratingCount === 0 && !row.canRate) return null;
  const rounded = Math.round(row.ratingAvg ?? 0);
  return (
    <div className="mt-1">
      {row.ratingCount > 0 ? (
        <div className="inline-flex items-center gap-1 text-[11.5px] text-ink-500">
          <span className="inline-flex">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={12}
                className={
                  i <= rounded
                    ? "text-amber-500 fill-amber-400"
                    : "text-ink-300"
                }
              />
            ))}
          </span>
          <span className="tabular-nums">
            {row.ratingAvg?.toFixed(1)} ({row.ratingCount})
          </span>
        </div>
      ) : (
        <div className="text-[11.5px] text-ink-400">No ratings yet</div>
      )}
      {row.canRate && (
        <div className="inline-flex items-center gap-1 mt-0.5">
          <span className="text-[10.5px] text-ink-400">
            {row.myStars ? "Your rating" : "Rate"} (anonymous):
          </span>
          <span className="inline-flex">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                disabled={busy}
                onClick={() => onRate(i)}
                title={
                  row.myStars === i
                    ? "Click again to withdraw your rating"
                    : `Rate ${i} star${i === 1 ? "" : "s"} — anonymous`
                }
                className="p-0.5 disabled:opacity-50"
              >
                <Star
                  size={13}
                  className={
                    i <= (row.myStars ?? 0)
                      ? "text-amber-500 fill-amber-400"
                      : "text-ink-300 hover:text-amber-400"
                  }
                />
              </button>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

export function ContractorDirectoryPage() {
  const { state } = useAuth();
  const isStaff =
    state.status === "authenticated" &&
    hasAnyRole(state.me, ["property_manager", "admin"]);
  const [rows, setRows] = useState<ContractorDirectoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [discipline, setDiscipline] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Staff record editing — the full company record, rendered as an
  // expanded editor row under the company.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    discipline: "general",
    businessLicenseNo: "",
    contactName: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    isActive: true,
  });
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  // Company id with a rating request in flight.
  const [ratingBusyId, setRatingBusyId] = useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = useState<{
    companyId: string;
    label: "Shared" | "Copied";
  } | null>(null);

  const toggleExpanded = (companyId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const reload = () =>
    api
      .get<{ data: ContractorDirectoryRow[] }>("/api/contractor-directory")
      .then((r) => setRows(r.data))
      .catch((err) => {
        if (err instanceof ApiError) setError(JSON.stringify(err.body));
        else setError(String(err));
      });

  useEffect(() => {
    void reload();
  }, []);

  const startEdit = (r: ContractorDirectoryRow) => {
    setEditingId(r.companyId);
    setEditError(null);
    setEditSuccess(null);
    setEditForm({
      name: r.name,
      discipline: r.discipline,
      businessLicenseNo: r.businessLicenseNo ?? "",
      contactName: r.contactName ?? "",
      phone: r.phone ?? "",
      email: r.email ?? "",
      website: r.website ?? "",
      address: r.address ?? "",
      isActive: r.isActive,
    });
  };

  const saveEdit = async (companyId: string) => {
    if (editForm.name.trim().length < 2) {
      setEditError("Company name is required.");
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      await api.patch(`/api/admin/contractors/${companyId}`, {
        name: editForm.name.trim(),
        discipline: editForm.discipline,
        businessLicenseNo: editForm.businessLicenseNo.trim() || null,
        contactName: editForm.contactName.trim() || null,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        website: editForm.website.trim() || null,
        address: editForm.address.trim() || null,
        isActive: editForm.isActive,
      });
      setEditingId(null);
      await reload();
      setEditSuccess(`${editForm.name.trim()} was updated.`);
    } catch (err) {
      const code =
        err instanceof ApiError
          ? (err.body as { error?: string })?.error
          : undefined;
      setEditError(
        code === "name_in_use"
          ? "Another company already uses that name."
          : code === "invalid_website"
            ? "That website doesn't look like a valid http(s) address."
            : code === "invalid_input"
              ? "Check the email address format."
              : "Save failed. Try again.",
      );
    } finally {
      setEditBusy(false);
    }
  };

  const shareContractor = async (r: ContractorDirectoryRow) => {
    const details = [
      r.name,
      disciplineLabel(r.discipline),
      r.contactName ? `Contact: ${r.contactName}` : null,
      r.phone ? `Phone: ${r.phone}` : null,
      r.email ? `Email: ${r.email}` : null,
      r.website ? `Website: ${r.website}` : null,
    ]
      .filter((line): line is string => Boolean(line))
      .join("\n");

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${r.name} contractor details`,
          text: details,
        });
        setShareFeedback({ companyId: r.companyId, label: "Shared" });
      } else {
        await navigator.clipboard.writeText(details);
        setShareFeedback({ companyId: r.companyId, label: "Copied" });
      }
      window.setTimeout(
        () =>
          setShareFeedback((current) =>
            current?.companyId === r.companyId ? null : current,
          ),
        1800,
      );
    } catch (err) {
      // Closing the native share sheet is intentional, not an error.
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError("Unable to share this contractor. Please try again.");
    }
  };

  const rate = async (r: ContractorDirectoryRow, stars: number) => {
    setRatingBusyId(r.companyId);
    try {
      if (r.myStars === stars) {
        // Clicking your current rating withdraws it.
        await api.del(`/api/contractors/${r.companyId}/ratings`);
      } else {
        await api.post(`/api/contractors/${r.companyId}/ratings`, { stars });
      }
      await reload();
    } catch {
      /* leave the row as-is; the user can retry */
    } finally {
      setRatingBusyId(null);
    }
  };

  const disciplines = useMemo(() => {
    const set = new Set((rows ?? []).map((r) => r.discipline));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (discipline !== "all" && r.discipline !== discipline) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.email ?? "").toLowerCase().includes(q) ||
        r.workTypes.some((w) => w.toLowerCase().includes(q)) ||
        r.priorJobSummaries.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [rows, query, discipline]);

  return (
    <div>
      <PageHeader
        eyebrow="Reference"
        title="Contractor Directory"
        subtitle="Construction companies that have worked on projects in the building — their contact information, how many projects they've been engaged on, and the type of work they've done. Use it as a starting point when hiring; the Association does not endorse or vouch for any company listed."
      />

      {error && <div className="alert-error">{error}</div>}
      {editSuccess && (
        <div className="alert-success mb-4" role="status">
          {editSuccess}
        </div>
      )}

      {rows === null && !error && <p className="muted">Loading…</p>}

      {rows !== null && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
              />
              <input
                className="input pl-9"
                placeholder="Search by company, email, or type of work…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <select
              className="select sm:w-56"
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
              aria-label="Filter by discipline"
            >
              <option value="all">All disciplines</option>
              {disciplines.map((d) => (
                <option key={d} value={d}>
                  {disciplineLabel(d)}
                </option>
              ))}
            </select>
          </div>
          {isStaff && (
            <div className="alert-info mb-4 text-[12.5px]">
              Property Managers and Platform Administrators can update a
              contractor’s company and contact information using{" "}
              <strong>Edit company</strong>.
            </div>
          )}

          {rows.length === 0 ? (
            <Card className="text-center py-10">
              <HardHat size={28} className="mx-auto text-ink-300 mb-3" />
              <p className="text-ink-600 font-medium mb-1">
                No contractors on record yet
              </p>
              <p className="text-sm text-ink-500">
                Companies appear here once an owner engages them on a
                project. Check back after the first projects go through.
              </p>
            </Card>
          ) : filtered.length === 0 ? (
            <Card className="text-center py-10">
              <p className="text-sm text-ink-500">
                No contractors match your search.
              </p>
            </Card>
          ) : (
            <Card>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Contact</th>
                      <th>Type of work</th>
                      <th className="text-right">Projects</th>
                      <th>Last engaged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <Fragment key={r.companyId}>
                        <tr>
                        <td>
                          <div className="font-medium text-ink-800">
                            {r.name}
                            {!r.isActive && (
                              <span className="badge ml-2">Inactive</span>
                            )}
                          </div>
                          <div className="text-xs text-ink-500 mt-0.5">
                            {disciplineLabel(r.discipline)}
                            {r.businessLicenseNo && (
                              <span className="text-ink-400">
                                {" · Lic. "}
                                {r.businessLicenseNo}
                              </span>
                            )}
                          </div>
                          <RatingRow
                            row={r}
                            busy={ratingBusyId === r.companyId}
                            onRate={(s) => void rate(r, s)}
                          />
                          {isStaff && (
                            <button
                              type="button"
                              onClick={() =>
                                editingId === r.companyId
                                  ? setEditingId(null)
                                  : startEdit(r)
                              }
                              className="inline-flex items-center gap-1 mt-1.5 text-[11.5px] font-medium text-brand-700 hover:underline"
                              title="Edit the contractor company record"
                            >
                              <Pencil size={11} />{" "}
                              {editingId === r.companyId
                                ? "Close editor"
                                : "Edit company"}
                            </button>
                          )}
                        </td>
                        <td>
                          {
                            <div className="flex flex-col gap-0.5 text-[13px]">
                              {r.contactName && (
                                <span className="inline-flex items-center gap-1.5 text-ink-700">
                                  <UserRound size={13} className="shrink-0" />
                                  {r.contactName}
                                </span>
                              )}
                              {r.email && (
                                <a
                                  href={`mailto:${r.email}`}
                                  className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                                >
                                  <Mail size={13} className="shrink-0" />
                                  {r.email}
                                </a>
                              )}
                              {r.phone && (
                                <a
                                  href={`tel:${r.phone}`}
                                  className="inline-flex items-center gap-1.5 text-ink-700 hover:underline"
                                >
                                  <Phone size={13} className="shrink-0" />
                                  {r.phone}
                                </a>
                              )}
                              {r.website && (
                                <a
                                  href={r.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                                >
                                  <Globe size={13} className="shrink-0" />
                                  {r.website.replace(/^https?:\/\//i, "")}
                                </a>
                              )}
                              {r.address && (
                                <span className="inline-flex items-center gap-1.5 text-ink-500">
                                  <MapPin size={13} className="shrink-0" />
                                  {r.address}
                                </span>
                              )}
                              {!r.contactName &&
                                !r.email &&
                                !r.phone &&
                                !r.website &&
                                !r.address && (
                                <span className="text-ink-400">—</span>
                              )}
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                                <button
                                  type="button"
                                  onClick={() => void shareContractor(r)}
                                  className="inline-flex items-center gap-1 text-[11.5px] text-brand-700 hover:underline"
                                  title={`Share ${r.name} contact details`}
                                  aria-label={`Share ${r.name} contact details`}
                                >
                                  {shareFeedback?.companyId === r.companyId &&
                                  shareFeedback.label === "Copied" ? (
                                    <Check size={11} />
                                  ) : (
                                    <Share2 size={11} />
                                  )}
                                  {shareFeedback?.companyId === r.companyId
                                    ? shareFeedback.label
                                    : "Share"}
                                </button>
                              </div>
                            </div>
                          }
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {r.workTypes.length > 0 ? (
                              r.workTypes.map((w) => (
                                <span
                                  key={w}
                                  className="inline-flex items-center rounded-full bg-sand-100 text-ink-600 px-2 py-0.5 text-[11px]"
                                >
                                  {w}
                                </span>
                              ))
                            ) : (
                              <span className="text-ink-400">—</span>
                            )}
                          </div>
                          {r.roles.length > 0 && (
                            <div className="text-[11px] text-ink-400 mt-1">
                              Engaged as{" "}
                              {r.roles
                                .map((role) => ROLE_LABELS[role] ?? role)
                                .join(", ")}
                            </div>
                          )}
                          {r.priorJobSummaries.length > 0 && (
                            <button
                              onClick={() => toggleExpanded(r.companyId)}
                              className="inline-flex items-center gap-1 mt-1.5 text-[11.5px] text-brand-700 hover:underline"
                            >
                              {expandedIds.has(r.companyId) ? (
                                <ChevronUp size={12} />
                              ) : (
                                <ChevronDown size={12} />
                              )}
                              Job history ({r.priorJobSummaries.length})
                            </button>
                          )}
                        </td>
                        <td className="text-right">
                          <span
                            className={cx(
                              "font-medium tabular-nums",
                              r.projectCount > 0
                                ? "text-ink-800"
                                : "text-ink-400",
                            )}
                          >
                            {r.projectCount}
                          </span>
                          {r.completedProjectCount > 0 && (
                            <div className="text-[11px] text-ink-400">
                              {r.completedProjectCount} completed
                            </div>
                          )}
                          {r.priorProjectCount > 0 && (
                            <div className="text-[11px] text-ink-400">
                              {r.priorProjectCount} from prior records
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-[13px] text-ink-600">
                          {r.lastEngagedAt ? (
                            new Date(r.lastEngagedAt).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                          ) : (
                            <span
                              className="text-ink-400"
                              title="Known from paper construction requests that predate the portal"
                            >
                              Prior records
                            </span>
                          )}
                        </td>
                        </tr>
                        {isStaff && editingId === r.companyId && (
                          <tr>
                            <td colSpan={5} className="bg-sand-50">
                              <div className="py-2 pl-2 max-w-3xl">
                                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400 mb-2">
                                  Edit company record
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  <label className="text-[11px] text-ink-500">
                                    Company name
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      value={editForm.name}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          name: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Discipline
                                    <select
                                      className="select py-1 text-[12.5px] mt-0.5"
                                      value={editForm.discipline}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          discipline: e.target.value,
                                        }))
                                      }
                                    >
                                      {Object.entries(DISCIPLINE_LABELS).map(
                                        ([code, label]) => (
                                          <option key={code} value={code}>
                                            {label}
                                          </option>
                                        ),
                                      )}
                                    </select>
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Business license
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      value={editForm.businessLicenseNo}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          businessLicenseNo: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Primary contact name
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      value={editForm.contactName}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          contactName: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Phone
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      value={editForm.phone}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          phone: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Email
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      type="email"
                                      value={editForm.email}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          email: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500">
                                    Website
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      placeholder="https://…"
                                      value={editForm.website}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          website: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="text-[11px] text-ink-500 sm:col-span-2">
                                    Address
                                    <input
                                      className="input py-1 text-[12.5px] mt-0.5"
                                      value={editForm.address}
                                      onChange={(e) =>
                                        setEditForm((f) => ({
                                          ...f,
                                          address: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                </div>
                                <label className="inline-flex items-center gap-1.5 mt-2 text-[12px] text-ink-600">
                                  <input
                                    type="checkbox"
                                    checked={editForm.isActive}
                                    onChange={(e) =>
                                      setEditForm((f) => ({
                                        ...f,
                                        isActive: e.target.checked,
                                      }))
                                    }
                                  />
                                  Active — listed for residents in the
                                  directory
                                </label>
                                {editError && (
                                  <p className="text-[11px] text-status-rejected-fg mt-1.5">
                                    {editError}
                                  </p>
                                )}
                                <div className="flex gap-1.5 mt-2">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    loading={editBusy}
                                    onClick={() => void saveEdit(r.companyId)}
                                  >
                                    <Check size={13} /> Save record
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={editBusy}
                                    onClick={() => setEditingId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {expandedIds.has(r.companyId) && (
                          <tr>
                            <td colSpan={5} className="bg-sand-50">
                              <div className="py-1.5 pl-2">
                                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400 mb-1.5">
                                  What they did — from the building's
                                  construction records
                                </div>
                                <ul className="flex flex-col gap-1">
                                  {r.priorJobSummaries.map((s, i) => (
                                    <li
                                      key={i}
                                      className="text-[12.5px] text-ink-600 leading-snug pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-ink-300"
                                    >
                                      {s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
